// Табличка на двері: основа + надпись (1–3 строки) + рамка, опуклий або врізаний текст, кріплення на скотч або шурупи.
// Две части — основа и надпись — для двухцветной печати (3MF / ZIP). QR — следующим шагом.
import { bedPicker, bedWarn, createStage, fitsBed, loadFont, scope, solid, textContours, toGeometry, type Ctx, type Model } from '@gen3d/core';
import type { CrossSection } from 'manifold-3d/manifold';

type Align = 'left' | 'center' | 'right';
type Line = { text: string; size: number };
type S = {
  W: number; H: number; R: number; T: number;
  lines: Line[]; font: string; align: Align;
  mode: 'raised' | 'engraved'; D: number;
  frame: boolean; FW: number; FG: number;
  mount: 'tape' | 'screws'; HD: number; HH: number; HI: number;
};
type NumKey = 'W' | 'H' | 'R' | 'T' | 'D' | 'FW' | 'FG' | 'HD' | 'HH' | 'HI';

// Шрифты лежат в /a/fonts/<id>.ttf. Все проверены на украинские буквы (ҐЄІЇ) и №.
const FONTS: [group: 'sans' | 'serif' | 'hand' | 'deco', id: string, name: string][] = [
  ['sans', 'rubik-700', 'Rubik'], ['sans', 'manrope-800', 'Manrope'], ['sans', 'montserrat-700', 'Montserrat'],
  ['sans', 'roboto-700', 'Roboto'], ['sans', 'ibm-plex-sans-700', 'IBM Plex Sans'], ['sans', 'pt-sans-700', 'PT Sans'],
  ['sans', 'nunito-800', 'Nunito'], ['sans', 'ubuntu-700', 'Ubuntu'], ['sans', 'exo-2-700', 'Exo 2'],
  ['sans', 'oswald-600', 'Oswald'], ['sans', 'comfortaa-700', 'Comfortaa'],
  ['serif', 'roboto-slab-700', 'Roboto Slab'], ['serif', 'bitter-700', 'Bitter'],
  ['serif', 'playfair-display-700', 'Playfair Display'], ['serif', 'yeseva-one-400', 'Yeseva One'],
  ['hand', 'caveat-700', 'Caveat'], ['hand', 'marck-script-400', 'Marck Script'], ['hand', 'neucha-400', 'Neucha'],
  ['hand', 'pacifico-400', 'Pacifico'], ['hand', 'lobster-400', 'Lobster'],
  ['deco', 'unbounded-700', 'Unbounded'], ['deco', 'russo-one-400', 'Russo One'], ['deco', 'rubik-mono-one-400', 'Rubik Mono One'],
  ['deco', 'ibm-plex-mono-600', 'IBM Plex Mono'], ['deco', 'press-start-2p-400', 'Press Start 2P'],
];

const T = {
  uk: {
    size: 'Розмір', W: 'Ширина', H: 'Висота', R: 'Радіус кутів', T: 'Товщина основи',
    presets: [['Кабінет', 200, 70], ['Квартира', 100, 50]] as [string, number, number][],
    text: 'Напис', font: 'Шрифт', line: 'Рядок', lineSize: 'Висота великих літер', align: 'Вирівнювання',
    left: 'Ліворуч', center: 'По центру', right: 'Праворуч',
    groups: { sans: 'Без засічок', serif: 'Із засічками', hand: 'Рукописні', deco: 'Декоративні' },
    relief: 'Рельєф', raised: 'Опуклий', engraved: 'Врізаний', Draised: 'Висота літер', Dengraved: 'Глибина літер',
    hintRaised: 'Два кольори без AMS: заміна пластику на висоті {T} мм.',
    hintEngraved: 'Літери втоплені в основу. Два кольори — файл 3MF для принтера з AMS.',
    frame: 'Рамка', frameOn: 'Рамка по краю', FW: 'Ширина рамки', FG: 'Відступ від краю',
    mount: 'Кріплення', tape: 'Скотч', screws: 'Шурупи', HD: 'Діаметр отвору', HH: 'Діаметр головки', HI: 'Від краю до центру отвору',
    hintTape: 'Рівна задня сторона — під двосторонній скотч.',
    hintScrews: 'Отвори з зенковкою під шурупи з потайною головкою.',
    mm: 'мм', min: 'мін.', max: 'макс.',
    fitOk: 'Напис вміщується', fitBad: 'Напис виходить за край — зменште його або збільште табличку', fitHole: 'Напис заходить на отвори',
    noGlyph: 'Немає в шрифті — пропущено: {c}',
    loading: 'Будую модель…', fontErr: 'Не вдалося завантажити шрифт',
    def: ['Кабінет 12', 'Бухгалтерія', ''],
  },
  ru: {
    size: 'Размер', W: 'Ширина', H: 'Высота', R: 'Радиус углов', T: 'Толщина основы',
    presets: [['Кабинет', 200, 70], ['Квартира', 100, 50]] as [string, number, number][],
    text: 'Надпись', font: 'Шрифт', line: 'Строка', lineSize: 'Высота заглавных букв', align: 'Выравнивание',
    left: 'Слева', center: 'По центру', right: 'Справа',
    groups: { sans: 'Без засечек', serif: 'С засечками', hand: 'Рукописные', deco: 'Декоративные' },
    relief: 'Рельеф', raised: 'Выпуклый', engraved: 'Врезной', Draised: 'Высота букв', Dengraved: 'Глубина букв',
    hintRaised: 'Два цвета без AMS: замена пластика на высоте {T} мм.',
    hintEngraved: 'Буквы утоплены в основу. Два цвета — файл 3MF для принтера с AMS.',
    frame: 'Рамка', frameOn: 'Рамка по краю', FW: 'Ширина рамки', FG: 'Отступ от края',
    mount: 'Крепление', tape: 'Скотч', screws: 'Шурупы', HD: 'Диаметр отверстия', HH: 'Диаметр головки', HI: 'От края до центра отверстия',
    hintTape: 'Ровная задняя сторона — под двусторонний скотч.',
    hintScrews: 'Отверстия с зенковкой под шурупы с потайной головкой.',
    mm: 'мм', min: 'мин.', max: 'макс.',
    fitOk: 'Надпись помещается', fitBad: 'Надпись выходит за край — уменьшите её или увеличьте табличку', fitHole: 'Надпись заходит на отверстия',
    noGlyph: 'Нет в шрифте — пропущено: {c}',
    loading: 'Строю модель…', fontErr: 'Не удалось загрузить шрифт',
    def: ['Кабинет 12', 'Бухгалтерия', ''],
  },
  en: {
    size: 'Size', W: 'Width', H: 'Height', R: 'Corner radius', T: 'Base thickness',
    presets: [['Office door', 200, 70], ['Apartment', 100, 50]] as [string, number, number][],
    text: 'Text', font: 'Font', line: 'Line', lineSize: 'Capital letter height', align: 'Alignment',
    left: 'Left', center: 'Center', right: 'Right',
    groups: { sans: 'Sans serif', serif: 'Serif', hand: 'Handwritten', deco: 'Display' },
    relief: 'Relief', raised: 'Raised', engraved: 'Engraved', Draised: 'Letter height', Dengraved: 'Letter depth',
    hintRaised: 'Two colors without AMS: swap filament at {T} mm.',
    hintEngraved: 'Letters are sunk into the base. For two colors use the 3MF on a printer with AMS.',
    frame: 'Border', frameOn: 'Border around the edge', FW: 'Border width', FG: 'Inset from edge',
    mount: 'Mounting', tape: 'Tape', screws: 'Screws', HD: 'Hole diameter', HH: 'Head diameter', HI: 'Edge to hole center',
    hintTape: 'Flat back for double-sided tape.',
    hintScrews: 'Countersunk holes for flat-head screws.',
    mm: 'mm', min: 'min', max: 'max',
    fitOk: 'Text fits', fitBad: 'Text runs past the edge — make it smaller or the sign larger', fitHole: 'Text overlaps the holes',
    noGlyph: 'Not in the font, skipped: {c}',
    loading: 'Building model…', fontErr: 'Could not load the font',
    def: ['Room 12', 'Accounting', ''],
  },
};

// [мин, макс, шаг]
const LIM: Record<NumKey, [number, number, number]> = {
  W: [30, 250, 1], H: [20, 250, 1], R: [0, 40, 0.5], T: [1.6, 10, 0.2],
  D: [0.4, 6, 0.2], FW: [1, 8, 0.5], FG: [0, 20, 0.5],
  HD: [2, 8, 0.5], HH: [4, 14, 0.5], HI: [5, 60, 1],
};
const SIZE: [number, number, number] = [4, 80, 0.5];

const esc = (s: string) => s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]!);
/** Текст-заготовка пресета: 0 — кабинет (три строки), 1 — квартира (крупный номер). */
const tpl = (t: { def: string[] }, i: number): Line[] => i === 1
  ? [{ text: '12', size: 28 }, { text: '', size: 9 }, { text: '', size: 7 }]
  : [{ text: t.def[0], size: 16 }, { text: t.def[1], size: 9 }, { text: t.def[2], size: 7 }];
const sameLines = (a: Line[], b: Line[]) => a.every((l, i) => l.text === b[i].text && l.size === b[i].size);
const clamp = (v: number, [a, b]: [number, number, number]) => Math.min(b, Math.max(a, v));
/**
 * Текст без символов, которых нет в шрифте (эмодзи, ★): вместо них шрифт рисует прямоугольник-заглушку.
 * Пробельные — пробелом (табуляция тоже была прямоугольником), невидимые служебные (селектор варианта
 * у эмодзи, ZWJ) — молча.
 */
function inFont(font: Awaited<ReturnType<typeof loadFont>>, text: string, miss: Set<string>) {
  let out = '';
  for (const ch of text.normalize('NFC')) {
    if (/\s/.test(ch)) out += ' ';
    else if (font.charToGlyphIndex(ch) > 0) out += ch;
    else if (!/[\p{M}\p{Cf}]/u.test(ch)) miss.add(ch);
  }
  return out;
}

/** Сховище сторінки (параметри в межах вкладки), якщо сторінка його дає. */
type Store = { load(): unknown; save(v: unknown): void };

export function mount(root: HTMLElement, { lang, store }: Ctx & { store?: Store }) {
  const t = T[lang] ?? T.uk;
  const s: S = {
    W: 200, H: 70, R: 6, T: 5,
    lines: tpl(t, 0),
    font: 'rubik-700', align: 'center',
    mode: 'raised', D: 3,
    frame: true, FW: 2, FG: 4,
    mount: 'tape', HD: 4, HH: 8, HI: 14,
  };
  // збережене в цій вкладці: кожне поле перевіряємо
  try {
    const v = (store?.load() as { s?: Record<string, any> } | null)?.s;
    if (v && typeof v === 'object') {
      for (const k of Object.keys(LIM) as NumKey[]) if (typeof v[k] === 'number' && isFinite(v[k])) s[k] = clamp(v[k], LIM[k]);
      if (FONTS.some(([, id]) => id === v.font)) s.font = v.font;
      if (['left', 'center', 'right'].includes(v.align)) s.align = v.align;
      if (v.mode === 'raised' || v.mode === 'engraved') s.mode = v.mode;
      if (v.mount === 'tape' || v.mount === 'screws') s.mount = v.mount;
      if (typeof v.frame === 'boolean') s.frame = v.frame;
      if (Array.isArray(v.lines) && v.lines.length === s.lines.length)
        v.lines.forEach((l: any, i: number) => {
          if (typeof l?.text === 'string') s.lines[i].text = l.text.slice(0, 40);
          if (typeof l?.size === 'number' && isFinite(l.size)) s.lines[i].size = clamp(l.size, SIZE);
        });
    }
  } catch { /* типові */ }

  const stage = root.querySelector<HTMLElement>('[data-stage]')!;
  const panel = root.querySelector<HTMLElement>('[data-params]')!;
  const viewer = createStage(stage, lang).viewer;

  // ---------- панель ----------
  const unit = `<i>${t.mm}</i>`;
  const rng = (k: NumKey, label: string) => {
    const [min, max, step] = LIM[k];
    return `<div class="rng" data-k="${k}"><span class="lbl" data-lbl>${label}</span><span class="rng-row">` +
      `<input type="range" min="${min}" max="${max}" step="${step}" value="${s[k]}" aria-label="${label}">` +
      `<span class="num"><input type="number" min="${min}" max="${max}" step="${step}" value="${s[k]}" inputmode="decimal" aria-label="${label}">${unit}</span></span>` +
      `<span class="minmax">${t.min} ${min} · ${t.max} ${max}</span></div>`;
  };
  const seg = (k: string, opts: [string, string][], cur: string) =>
    `<div class="seg wide" data-seg="${k}">${opts.map(([v, l]) => `<button type="button" data-v="${v}" class="${v === cur ? 'on' : ''}">${l}</button>`).join('')}</div>`;
  const fontOpts = (['sans', 'serif', 'hand', 'deco'] as const).map((g) =>
    `<optgroup label="${t.groups[g]}">${FONTS.filter((f) => f[0] === g).map(([, id, n]) =>
      `<option value="${id}"${id === s.font ? ' selected' : ''}>${n}</option>`).join('')}</optgroup>`).join('');

  panel.innerHTML =
    `<details class="group" open><summary>${t.size}</summary>` +
      `<div class="chips" data-presets>${t.presets.map(([n, w, h], i) => `<button type="button" class="chip" data-i="${i}" data-w="${w}" data-h="${h}">${n} ${w}×${h}</button>`).join('')}</div>` +
      rng('W', t.W) + rng('H', t.H) + rng('R', t.R) + rng('T', t.T) + '</details>' +
    `<details class="group" open><summary>${t.text}</summary>` +
      `<label class="field flat"><span class="lbl">${t.font}</span><select data-font>${fontOpts}</select></label>` +
      s.lines.map((l, i) => `<div class="line" data-line="${i}"><span class="lbl">${t.line} ${i + 1}</span><span class="line-row">` +
        `<input type="text" value="${esc(l.text)}" maxlength="40" aria-label="${t.line} ${i + 1}">` +
        `<span class="num" title="${t.lineSize}"><input type="number" min="${SIZE[0]}" max="${SIZE[1]}" step="${SIZE[2]}" value="${l.size}" inputmode="decimal" aria-label="${t.line} ${i + 1}: ${t.lineSize}">${unit}</span></span></div>`).join('') +
      `<span class="minmax">${t.lineSize}, ${t.mm}</span>` +
      `<span class="lbl gap">${t.align}</span>` + seg('align', [['left', t.left], ['center', t.center], ['right', t.right]], s.align) + '</details>' +
    `<details class="group" open><summary>${t.relief}</summary>` +
      seg('mode', [['raised', t.raised], ['engraved', t.engraved]], s.mode) + rng('D', t.Draised) + '<p class="hint" data-hint-mode></p></details>' +
    `<details class="group"><summary>${t.frame}</summary>` +
      `<label class="switch"><input type="checkbox" data-frame${s.frame ? ' checked' : ''}><span>${t.frameOn}</span></label>` +
      `<div data-frame-opts>${rng('FW', t.FW)}${rng('FG', t.FG)}</div></details>` +
    `<details class="group"><summary>${t.mount}</summary>` +
      seg('mount', [['tape', t.tape], ['screws', t.screws]], s.mount) +
      `<div data-screw-opts>${rng('HD', t.HD)}${rng('HH', t.HH)}${rng('HI', t.HI)}</div><p class="hint" data-hint-mount></p></details>`;

  const $ = <E extends Element = HTMLElement>(q: string) => panel.querySelector<E>(q)!;

  panel.querySelectorAll<HTMLElement>('.rng').forEach((el) => {
    const k = el.dataset.k as NumKey;
    const [r, n] = el.querySelectorAll('input');
    const set = (raw: string) => {
      // пустое или не число — прежнее значение (раньше сбрасывалось на минимум)
      const x = raw.trim() === '' ? NaN : +raw;
      const v = Number.isFinite(x) ? clamp(x, LIM[k]) : s[k];
      s[k] = v; r.value = n.value = String(v); sync(); rebuild();
    };
    r.oninput = () => set(r.value);
    n.onchange = () => set(n.value);
  });
  const setNum = (k: NumKey, v: number) => {
    s[k] = clamp(v, LIM[k]);
    panel.querySelectorAll<HTMLInputElement>(`[data-k="${k}"] input`).forEach((i) => (i.value = String(s[k])));
  };

  $('[data-presets]').onclick = (e) => {
    const b = (e.target as HTMLElement).closest<HTMLElement>('[data-w]');
    if (!b) return;
    setNum('W', +b.dataset.w!); setNum('H', +b.dataset.h!);
    // текст меняем, только пока он из заготовки: свой текст пользователя не трогаем
    const i = +b.dataset.i!;
    if ([0, 1].some((j) => sameLines(s.lines, tpl(t, j)))) {
      s.lines = tpl(t, i);
      panel.querySelectorAll<HTMLElement>('[data-line]').forEach((el, j) => {
        const [txt, num] = el.querySelectorAll('input');
        txt.value = s.lines[j].text; num.value = String(s.lines[j].size);
      });
    }
    reframe = true; sync(); rebuild();
  };
  panel.querySelectorAll<HTMLElement>('[data-line]').forEach((el) => {
    const i = +el.dataset.line!;
    const [txt, num] = el.querySelectorAll('input');
    txt.oninput = () => { s.lines[i].text = txt.value; rebuild(); };
    num.onchange = () => {
      const x = num.value.trim() === '' ? NaN : +num.value;
      if (Number.isFinite(x)) s.lines[i].size = clamp(x, SIZE);
      num.value = String(s.lines[i].size); rebuild();
    };
  });
  $<HTMLSelectElement>('[data-font]').onchange = (e) => { s.font = (e.target as HTMLSelectElement).value; rebuild(); };
  panel.querySelectorAll<HTMLElement>('[data-seg]').forEach((el) => {
    el.onclick = (e) => {
      const b = (e.target as HTMLElement).closest<HTMLElement>('[data-v]');
      if (!b) return;
      el.querySelectorAll('button').forEach((x) => x.classList.toggle('on', x === b));
      (s as any)[el.dataset.seg!] = b.dataset.v;
      sync(); rebuild();
      if (el.dataset.seg === 'mount' && b.dataset.v === 'screws') reveal($('[data-screw-opts]'));
    };
  });
  $<HTMLInputElement>('[data-frame]').onchange = (e) => {
    s.frame = (e.target as HTMLInputElement).checked; sync(); rebuild();
    if (s.frame) reveal($('[data-frame-opts]'));
  };
  /** Прокрутити до блоку, що щойно відкрився, — щоб не ховався під кнопками завантаження. */
  const reveal = (el: HTMLElement) => {
    const bar = document.querySelector<HTMLElement>('.actions');
    el.style.scrollMarginBottom = `${(bar?.offsetHeight ?? 0) + 12}px`;
    el.scrollIntoView({ block: 'nearest', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
  };
  let bed = bedPicker(panel, lang, (b) => { bed = b; viewer.setBed(b.x, b.y); rebuild(); });
  viewer.setBed(bed.x, bed.y);

  /** Подписи и видимость, зависящие от выбора. */
  function sync() {
    $('[data-k="D"] [data-lbl]').textContent = s.mode === 'raised' ? t.Draised : t.Dengraved;
    $('[data-hint-mode]').textContent = s.mode === 'raised' ? t.hintRaised.replace('{T}', String(s.T)) : t.hintEngraved;
    $('[data-hint-mount]').textContent = s.mount === 'tape' ? t.hintTape : t.hintScrews;
    $('[data-frame-opts]').hidden = !s.frame;
    $('[data-screw-opts]').hidden = s.mount !== 'screws';
  }

  // ---------- геометрия ----------
  const fontUrl = (id: string) => `/a/fonts/${id}.ttf`;
  const dims = stage.querySelector<HTMLElement>('[data-dims]')!;
  const fit = stage.querySelector<HTMLElement>('[data-fit]')!;
  let current: Promise<Model> | null = null;
  const setReady = (on: boolean) => root.querySelectorAll<HTMLButtonElement>('[data-download]').forEach((b) => { b.disabled = !on; });
  let seq = 0;
  let reframe = true;
  let timer = 0;

  function rebuild() {
    clearTimeout(timer);
    timer = window.setTimeout(() => {
      const my = ++seq;
      fit.className = 'fit'; fit.textContent = t.loading;
      const p = build({ ...s, lines: s.lines.map((l) => ({ ...l })) });
      try { store?.save({ s }); } catch { /* сховище недоступне */ }
      current = p;
      p.then((m) => {
        if (my !== seq) return;
        viewer.setParts(m.parts);
        const warn = fitsBed(bed, s.W, s.H, s.T + (s.mode === 'raised' ? s.D : 0)) ? m.warn : bedWarn(lang, bed);
        fit.className = warn ? 'fit bad' : 'fit';
        fit.textContent = warn || t.fitOk;
        // надпись за краем табличку портит — такую не скачать
        setReady(!m.over);
        if (reframe) { viewer.frame(s.W, s.H, s.T + s.D); reframe = false; }
        dims.innerHTML = `<b>${s.W}</b> × <b>${s.H}</b> × <b>${+(s.T + (s.mode === 'raised' ? s.D : 0)).toFixed(1)}</b> ${t.mm}`;
      }, (e) => {
        console.error(e);
        if (my === seq) { fit.className = 'fit bad'; fit.textContent = t.fontErr; }
      });
    }, 60);
  }

  async function build(p: S): Promise<Model & { warn: string; over: boolean }> {
    const [M, font] = await Promise.all([solid(), loadFont(fontUrl(p.font))]);
    const { CrossSection: CS, Manifold } = M;
    const k = scope();
    try {
      const segs = 64;
      const R = Math.min(p.R, p.W / 2 - 0.01, p.H / 2 - 0.01);
      const plate = R > 0.05
        ? k(k(CS.square([p.W - 2 * R, p.H - 2 * R], true)).offset(R, 'Round', 2, segs))
        : k(CS.square([p.W, p.H], true));

      // Поле для надписи: внутри рамки (или отступа) и, при шурупах, между отверстиями.
      const edge = p.frame ? p.FG + p.FW + 2 : 4;
      const holeX = p.W / 2 - p.HI; // центр отверстия по X (±)
      const holeClear = p.mount === 'screws' ? p.HH / 2 + 1.5 : 0;

      // строки: базовые линии по высоте заглавных, межстрочный просвет — 0.6 высоты следующей строки
      const miss = new Set<string>();
      const lines = p.lines.map((l) => ({ ...l, text: inFont(font, l.text, miss).trim() })).filter((l) => l.text);
      const shapes = lines.map((l) => ({ l, sh: textContours(font, l.text, l.size) }));
      const total = shapes.reduce((sum, { l }, i) => sum + l.size + (i ? l.size * 0.6 : 0), 0);
      let top = total / 2;
      const detail2d: CrossSection[] = [];
      let over = false, onHole = false;
      for (const [i, { l, sh }] of shapes.entries()) {
        if (i) top -= l.size * 0.6;
        const base = top - l.size;
        top = base;
        if (!sh.contours.length) continue;
        const w = sh.maxX - sh.minX;
        const left = -p.W / 2 + edge, right = p.W / 2 - edge;
        const dx = p.align === 'left' ? left - sh.minX : p.align === 'right' ? right - sh.maxX : -(sh.minX + sh.maxX) / 2;
        const x0 = sh.minX + dx, x1 = sh.maxX + dx, y0 = sh.minY + base, y1 = sh.maxY + base;
        if (x0 < left - 0.01 || x1 > right + 0.01 || y0 < -p.H / 2 + edge || y1 > p.H / 2 - edge || w > right - left) over = true;
        if (holeClear && Math.min(y1, 0 + holeClear) > Math.max(y0, -holeClear) && (x1 > holeX - holeClear || x0 < -holeX + holeClear)) onHole = true;
        detail2d.push(k(k(new CS(sh.contours, 'NonZero')).translate([dx, base])));
      }
      if (p.frame) {
        const outer = k(plate.offset(-p.FG, 'Round', 2, segs));
        const inner = k(plate.offset(-p.FG - p.FW, 'Round', 2, segs));
        detail2d.push(k(outer.subtract(inner)));
      }


      const engraved = p.mode === 'engraved';
      const D = engraved ? Math.min(p.D, p.T - 0.8) : p.D;
      let base = k(plate.extrude(p.T));
      const art = detail2d.length ? k(CS.union(detail2d)) : null;
      let detail = art && !art.isEmpty()
        ? k(k(art.extrude(D)).translate([0, 0, engraved ? p.T - D : p.T]))
        : null;
      if (engraved && detail) base = k(base.subtract(detail));

      if (p.mount === 'screws') {
        const sink = Math.max(0, (p.HH - p.HD) / 2); // зенковка 90°
        const cut: any[] = [];
        for (const x of [-holeX, holeX]) {
          cut.push(k(k(Manifold.cylinder(p.T + 2, p.HD / 2, p.HD / 2, 48)).translate([x, 0, -1])));
          if (sink) cut.push(k(k(Manifold.cylinder(sink + 0.001, p.HD / 2, p.HH / 2, 48)).translate([x, 0, p.T - sink])));
          cut.push(k(k(Manifold.cylinder(p.D + 1, p.HH / 2, p.HH / 2, 48)).translate([x, 0, p.T - 0.001])));
        }
        const holes = k(Manifold.union(cut));
        base = k(base.subtract(holes));
        if (detail) detail = k(detail.subtract(holes));
      }
      if (detail?.isEmpty()) detail = null;

      const baseG = toGeometry(base);
      const parts: Model['parts'] = [{ name: 'base', role: 'base', geometry: baseG }];
      if (detail) parts.push({ name: 'text', role: 'detail', geometry: toGeometry(detail) });
      // одним телом: опуклий — основа + літери; врізаний — основа з заглибленням
      const single = detail && !engraved ? toGeometry(k(base.add(detail))) : baseG;
      const warn = [over ? t.fitBad : onHole ? t.fitHole : '', miss.size ? t.noGlyph.replace('{c}', [...miss].join(' ')) : ''].filter(Boolean).join(' · ');
      return { name: `sign-${p.W}x${p.H}`, parts, single, warn, over };
    } finally {
      k.free();
    }
  }

  sync();
  rebuild();
  return { model: () => current ?? build(s) };
}
